import {
  ApiRequestMeta,
  ParsedApiError,
  parseApiError,
} from "@jugl-web/rest-api";
import { isPageUnloading, useToast, useTranslations } from "@jugl-web/utils";
import capitalize from "lodash/capitalize";
import { useCallback, useContext } from "react";
import { ApiResultHandlingContext } from "../providers/ApiResultHandlingProvider/ApiResultHandlingContext";

const hasLegacySilentErrorFlag = (rawError: unknown): boolean =>
  !!(rawError as { config?: { silentError?: boolean } } | null | undefined)
    ?.config?.silentError;

export const useApiResultHandler = () => {
  const { onSpecialError, onErrorReported } = useContext(
    ApiResultHandlingContext
  );
  const { toast } = useToast();
  const { t } = useTranslations();

  /**
   * Resolves the localized user-facing message for any API error — for inline
   * error UI (e.g. DataLoadingWrapper) or as the default toast text.
   */
  const getApiErrorMessage = useCallback(
    (error: unknown): string => {
      const parsed = parseApiError(error);
      if (parsed.type === "network") {
        return t({
          id: "feedback.no-internet-connection",
          defaultMessage: "No internet connection",
        });
      }
      if (parsed.type === "http") {
        if (parsed.status === 401) {
          return t({
            id: "feedback.session-expired",
            defaultMessage: "Your session expired, please login again",
          });
        }
        // Raw 5xx text can carry internal backend detail; machine codes can't.
        if (parsed.serverMessage && parsed.status < 500) {
          return capitalize(parsed.serverMessage);
        }
        if (parsed.code) {
          return capitalize(parsed.code.replaceAll("_", " "));
        }
        if (parsed.status === 403) {
          return t({
            id: "feedback.no-permissions-to-perform-action",
            defaultMessage: "You don't have permissions to perform this action",
          });
        }
      }
      return t({
        id: "feedback.something-went-wrong",
        defaultMessage: "Something went wrong",
      });
    },
    [t]
  );

  /**
   * Runs the error pipeline on an already-caught or globally-intercepted error:
   * parse → drop cancelled → report → onError → silent → app special routing →
   * toast. Returns the parsed error.
   */
  const handleApiError = useCallback(
    (rawError: unknown, meta: ApiRequestMeta = {}): ParsedApiError => {
      const error = parseApiError(rawError);
      // A hard navigation is in flight: the browser aborts pending requests, and axios
      // reports those as plain network errors rather than cancellations. The document is
      // going away, so nothing here is worth reporting or showing.
      if (
        isPageUnloading() ||
        (error.type === "network" && error.simulated) ||
        (error.type === "unknown" && error.canceled)
      ) {
        return error;
      }
      // Legacy endpoint-level `silentError` is an alias for `meta.silent`. It
      // must not pre-empt `meta.onError` — the call site's handler always runs.
      const isSilenced = !!meta.silent || hasLegacySilentErrorFlag(error.raw);
      if (!isSilenced) {
        onErrorReported?.(error);
      }
      if (meta.onError && meta.onError(error) !== "unhandled") {
        return error;
      }
      if (isSilenced) {
        return error;
      }
      // App-level policy outranks `meta.getErrorMessage`: an error suppressed
      // here (403, anonymous 401, inactive plan) shows no toast at all, even
      // when the call site supplied its own message.
      if (onSpecialError && onSpecialError(error) !== "unhandled") {
        return error;
      }
      toast(meta.getErrorMessage?.(error) ?? getApiErrorMessage(error), {
        variant: "error",
      });
      return error;
    },
    [toast, getApiErrorMessage, onSpecialError, onErrorReported]
  );

  return { handleApiError, getApiErrorMessage };
};
